import { ArrayElement } from 'core/actions/Action'
import { nonPaginatedVolumesSelector, volumesSelector } from '../selectors'

export type VolumeSelectorModel =
  | ArrayElement<ReturnType<typeof volumesSelector>>
  | ArrayElement<ReturnType<typeof nonPaginatedVolumesSelector>>

/**
 * Determines if a volume can be attached based on its multiattach capability and status.
 *
 * - Regular volumes: Can only attach when status is 'available'
 * - Multiattach volumes: Can attach when status is 'available' or 'in-use' (can accept additional attachments)
 *
 * @param volume - The volume object to check
 * @returns boolean - Whether the volume can be attached
 */
// VOLUTILS-FR-001, VOLUTILS-FR-002: Attachability by multiattach capability and status
export const canAttachVolume = (volume: VolumeSelectorModel) => {
  if (volume?.multiattach) {
    return ['available', 'in-use'].includes(volume?.status)
  }
  return volume?.status === 'available'
}

/**
 * Extracts the Glance image ID from a backing volume's metadata.
 * Returns null if the volume is not a backing image volume.
 *
 * Single source: volume metadata.glance_image_id. Volume name is never
 * consulted for backing-image detection.
 *
 * @param volume - The volume object to check
 * @returns The Glance image ID if this is a backing volume, null otherwise
 */
// VOLUTILS-FR-003: Glance image ID extraction (metadata-only)
export const getBackingImageId = (volume: VolumeSelectorModel): string | null => {
  return volume?.metadata?.glance_image_id || null
}

/**
 * Determines if a volume is a backing volume for a Glance image.
 * Backing image volumes are auto-created system volumes and cannot be deleted directly.
 *
 * @param volume - The volume object to check
 * @returns boolean - Whether the volume is a backing image volume
 */
// VOLUTILS-FR-004: Glance image ID extraction → boolean backing-volume check
export const isBackingImageVolume = (volume: VolumeSelectorModel) =>
  getBackingImageId(volume) !== null

/**
 * Badge variant type for volume status display
 */
export type BadgeVariant = 'warning' | 'success' | 'primary' | 'error' | 'unknown'

/**
 * Maps volume status to badge variant for visual display.
 *
 * Status to variant mappings:
 * - 'warning': creating, attaching, detaching, deleting, awaiting-transfer, backing-up,
 *              restoring-backup, downloading, uploading, retyping, extending
 * - 'success': available, in-use
 * - 'primary': reserved, maintenance
 * - 'error': error, error_deleting, error_backing-up, error_restoring, error_extending
 * - 'unknown': any other status
 *
 * @param status - The volume status string
 * @returns The badge variant for the status
 */
// VOLUTILS-FR-005: Volume status → badge variant mapping ('unknown' fallback)
export const getBadgeVariant = (status: string): BadgeVariant => {
  return ({
    creating: 'warning',
    available: 'success',
    reserved: 'primary',
    attaching: 'warning',
    detaching: 'warning',
    'in-use': 'success',
    maintenance: 'primary',
    deleting: 'warning',
    'awaiting-transfer': 'warning',
    error: 'error',
    error_deleting: 'error',
    'backing-up': 'warning',
    'restoring-backup': 'warning',
    'error_backing-up': 'error',
    error_restoring: 'error',
    error_extending: 'error',
    downloading: 'warning',
    uploading: 'warning',
    retyping: 'warning',
    extending: 'warning',
  }[status] || 'unknown') as BadgeVariant
}

/**
 * Maps volume status to human-readable display text.
 *
 * Converts status codes to user-friendly text:
 * - 'creating' → 'Creating'
 * - 'in-use' → 'In-Use'
 * - 'awaiting-transfer' → 'Awaiting Transfer'
 * - etc.
 *
 * @param status - The volume status string
 * @returns The human-readable text for the status
 */
// VOLUTILS-FR-006: Volume status → display text mapping (raw status fallback)
export const getBadgeText = (status: string): string => {
  const text =
    {
      creating: 'Creating',
      available: 'Available',
      reserved: 'Reserved',
      attaching: 'Attaching',
      detaching: 'Detaching',
      'in-use': 'In-Use',
      maintenance: 'Maintenance',
      deleting: 'Deleting',
      'awaiting-transfer': 'Awaiting Transfer',
      error: 'Error',
      error_deleting: 'Error Deleting',
      'backing-up': 'Backing Up',
      'restoring-backup': 'Restoring Backup',
      'error_backing-up': 'Error Backing Up',
      error_restoring: 'Error Restoring',
      error_extending: 'Error Extending',
      downloading: 'Downloading',
      uploading: 'Uploading',
      retyping: 'Retyping',
      extending: 'Extending',
    }[status] || status
  return text
}
